'use client';

import Image from 'next/image';
import { useTranslations } from 'next-intl';

import { RegistrationFlow, type PrefetchedRegistrationData } from '@/tenants/_shared/registration';

import { uwrRegistrationConfig } from './config';

interface RegisterPageProps {
  prefetchedData?: PrefetchedRegistrationData;
}

/**
 * UWR register page: split shell around the shared engine — fixed hero panel
 * on the left, scrollable registration flow on the right (DGR/DGD pattern,
 * UWR light theme).
 */
export function RegisterPage({ prefetchedData }: RegisterPageProps) {
  const t = useTranslations('uwrRegister');

  return (
    <div
      className="notranslate min-h-dvh overflow-x-clip bg-stone-50 lg:h-dvh lg:flex lg:flex-col lg:overflow-hidden"
      translate="no"
    >
      <div className="lg:flex lg:min-h-0 lg:flex-1">
        {/* Left side — hero image (fixed, desktop only) */}
        <div className="relative hidden flex-shrink-0 lg:block lg:w-1/2 xl:w-[55%]">
          <Image
            src="/images/uwr/HeroImageUWR.jpg"
            alt={t('heroTitle')}
            fill
            className="object-cover"
            priority
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/10" />

          {/* Overlay content */}
          <div className="absolute inset-x-0 bottom-0 p-12">
            <div className="max-w-lg">
              <p className="mb-3 text-sm font-medium uppercase tracking-wide text-primary">
                {t('eventDate')}
              </p>
              <h2 className="mb-4 text-4xl font-bold leading-tight text-white">{t('heroTitle')}</h2>
              <p className="text-lg text-white/90">{t('heroSubtitle')}</p>
            </div>
          </div>
        </div>

        {/* Right side — registration flow (scrollable) */}
        <div className="flex w-full flex-col overflow-x-clip bg-stone-50 lg:w-1/2 lg:overflow-y-auto lg:pt-20 xl:w-[45%]">
          {/* Mobile hero strip */}
          <div className="relative h-40 sm:h-44 lg:hidden">
            <Image
              src="/images/uwr/HeroImageUWR.jpg"
              alt={t('heroTitle')}
              fill
              className="object-cover"
              priority
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-transparent" />
            <div className="absolute inset-x-0 bottom-0 p-4 pt-12 sm:p-6 sm:pt-16">
              <p className="mb-1 text-xs font-medium uppercase tracking-wide text-primary">
                {t('eventDate')}
              </p>
              <h1 className="text-xl font-bold text-white sm:text-2xl">{t('heroTitle')}</h1>
            </div>
          </div>

          <div className="flex flex-col px-4 py-5 sm:px-6 sm:py-6 md:px-12 lg:flex-1 lg:px-10 lg:py-8 xl:px-16">
            <RegistrationFlow config={uwrRegistrationConfig} prefetchedData={prefetchedData} />
          </div>
        </div>
      </div>
    </div>
  );
}
